import { useState } from "react";
import { Receipt } from "lucide-react";
import { ApiError, type Order, type Store } from "../../lib/api";
import { formatMoney } from "../../lib/money";
import { useOrders, useUpdateOrderStatus } from "../../lib/store-api";
import { Badge, type BadgeProps } from "../../components/ui/Badge";
import { Button } from "../../components/ui/Button";
import { Dialog } from "../../components/ui/Dialog";
import { EmptyState } from "../../components/ui/EmptyState";
import { Spinner } from "../../components/ui/Spinner";

const STATUS: Record<Order["status"], { label: string; variant: BadgeProps["variant"] }> = {
  pending: { label: "Awaiting payment", variant: "warning" },
  paid: { label: "Paid — to fulfil", variant: "primary" },
  fulfilled: { label: "Fulfilled", variant: "success" },
  cancelled: { label: "Cancelled", variant: "default" },
};

const PAYMENT_LABEL: Record<Order["paymentMethod"], string> = {
  bank_transfer: "Bank transfer",
  online: "Online",
};

function formatDate(iso: string): string {
  return new Date(iso).toLocaleString(undefined, {
    day: "numeric",
    month: "short",
    hour: "2-digit",
    minute: "2-digit",
  });
}

export function OrdersTab({ siteId }: { siteId: string; store: Store }) {
  const { data: orders, isPending, error } = useOrders(siteId);
  const [openId, setOpenId] = useState<string | null>(null);
  const open = orders?.find((o) => o.id === openId) ?? null;

  if (isPending) {
    return (
      <div className="loading-center" style={{ padding: "var(--z-space-12)" }}>
        <Spinner />
      </div>
    );
  }
  if (error) {
    return (
      <div className="form-error" role="alert">
        Could not load orders.
      </div>
    );
  }
  if (!orders || orders.length === 0) {
    return (
      <EmptyState
        icon={<Receipt size={28} />}
        title="No orders yet"
        description="When shoppers check out, their orders appear here and you'll get an email for each one."
      />
    );
  }

  return (
    <section aria-label="Orders">
      <div className="order-table" role="table" aria-label="Orders">
        <div className="order-table__head" role="row">
          <span role="columnheader">Order</span>
          <span role="columnheader">Customer</span>
          <span role="columnheader">Payment</span>
          <span role="columnheader">Status</span>
          <span role="columnheader" className="order-table__num">
            Total
          </span>
        </div>
        {orders.map((order) => {
          const status = STATUS[order.status];
          return (
            <button
              key={order.id}
              type="button"
              role="row"
              className="order-table__row"
              onClick={() => setOpenId(order.id)}
            >
              <span role="cell">
                <strong>{order.number}</strong>
                <span className="text-muted text-sm">{formatDate(order.createdAt)}</span>
              </span>
              <span role="cell">
                {order.customer.name}
                <span className="text-muted text-sm">
                  {order.items.reduce((n, i) => n + i.quantity, 0)} items
                </span>
              </span>
              <span role="cell">{PAYMENT_LABEL[order.paymentMethod]}</span>
              <span role="cell">
                <Badge variant={status.variant} dot>
                  {status.label}
                </Badge>
              </span>
              <span role="cell" className="order-table__num">
                {formatMoney(order.totalMinor, order.currency)}
              </span>
            </button>
          );
        })}
      </div>

      {open ? <OrderDetail siteId={siteId} order={open} onClose={() => setOpenId(null)} /> : null}
    </section>
  );
}

function OrderDetail({
  siteId,
  order,
  onClose,
}: {
  siteId: string;
  order: Order;
  onClose: () => void;
}) {
  const update = useUpdateOrderStatus(siteId);
  const [confirmCancel, setConfirmCancel] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const setStatus = (status: "paid" | "fulfilled" | "cancelled") => {
    setError(null);
    update.mutate(
      { orderId: order.id, status },
      {
        onSuccess: () => setConfirmCancel(false),
        onError: (err) => setError(err instanceof ApiError ? err.message : "Could not update the order."),
      },
    );
  };

  const canCancel = order.status === "pending" || order.status === "paid";

  return (
    <Dialog
      open
      size="lg"
      title={`Order ${order.number}`}
      onClose={onClose}
      footer={
        <>
          {canCancel && !confirmCancel ? (
            <Button variant="danger-text" onClick={() => setConfirmCancel(true)}>
              Cancel order
            </Button>
          ) : null}
          {confirmCancel ? (
            <Button variant="danger" loading={update.isPending} onClick={() => setStatus("cancelled")}>
              Confirm cancel and restock
            </Button>
          ) : null}
          {order.status === "pending" ? (
            <Button loading={update.isPending && !confirmCancel} onClick={() => setStatus("paid")}>
              Mark as paid
            </Button>
          ) : null}
          {order.status === "paid" ? (
            <Button loading={update.isPending && !confirmCancel} onClick={() => setStatus("fulfilled")}>
              Mark as fulfilled
            </Button>
          ) : null}
        </>
      }
    >
      <div className="order-detail">
        <div className="order-detail__meta">
          <Badge variant={STATUS[order.status].variant} dot>
            {STATUS[order.status].label}
          </Badge>
          <span className="text-muted text-sm">
            {PAYMENT_LABEL[order.paymentMethod]} · placed {formatDate(order.createdAt)}
          </span>
        </div>
        {order.status === "pending" && order.paymentMethod === "bank_transfer" ? (
          <p className="order-detail__hint">
            Check your bank for a transfer of {formatMoney(order.totalMinor, order.currency)} with reference{" "}
            <strong>{order.number}</strong>, then mark the order as paid.
          </p>
        ) : null}

        <ul className="order-detail__items">
          {order.items.map((item, i) => (
            <li key={`${item.productId}-${i}`}>
              <div className="order-detail__thumb">{item.image ? <img src={item.image} alt="" /> : null}</div>
              <div className="order-detail__name">
                <strong>{item.name}</strong>
                {item.options && Object.keys(item.options).length > 0 ? (
                  <span className="text-muted text-sm">
                    {Object.entries(item.options)
                      .map(([k, v]) => `${k}: ${v}`)
                      .join(" · ")}
                  </span>
                ) : null}
              </div>
              <span className="text-muted">× {item.quantity}</span>
              <span>{formatMoney(item.lineTotalMinor, order.currency)}</span>
            </li>
          ))}
        </ul>

        <dl className="order-detail__totals">
          <div>
            <dt>Subtotal</dt>
            <dd>{formatMoney(order.subtotalMinor, order.currency)}</dd>
          </div>
          <div>
            <dt>{order.delivery.name}</dt>
            <dd>{order.deliveryMinor === 0 ? "Free" : formatMoney(order.deliveryMinor, order.currency)}</dd>
          </div>
          <div className="order-detail__total">
            <dt>Total</dt>
            <dd>{formatMoney(order.totalMinor, order.currency)}</dd>
          </div>
        </dl>

        <div className="order-detail__cols">
          <div>
            <h4>Customer</h4>
            <p>
              {order.customer.name}
              <br />
              <a href={`mailto:${order.customer.email}`}>{order.customer.email}</a>
              <br />
              <a href={`tel:${order.customer.phone}`}>{order.customer.phone}</a>
            </p>
          </div>
          <div>
            <h4>{order.delivery.pickup ? "Pickup" : "Deliver to"}</h4>
            {order.shippingAddress ? (
              <p>
                {order.shippingAddress.line1}
                <br />
                {order.shippingAddress.city}
                {order.shippingAddress.state ? `, ${order.shippingAddress.state}` : ""}
                {order.shippingAddress.notes ? (
                  <>
                    <br />
                    <span className="text-muted">Note: {order.shippingAddress.notes}</span>
                  </>
                ) : null}
              </p>
            ) : (
              <p className="text-muted">Customer will pick up in store.</p>
            )}
          </div>
        </div>

        {error ? (
          <div className="form-error" role="alert">
            {error}
          </div>
        ) : null}
      </div>
    </Dialog>
  );
}
